import type { ProfileConfig } from "@worker/config-schema";
import { el } from "../lib/dom";
import { renderProfileParts } from "./parts";

/**
 * Magazine template — editorial print layout.
 *
 *   ┌──────────────────────────────────────┐
 *   │ ISSUE #001             [pfp tile]    │
 *   │                                      │
 *   │ E   enahs lorem ipsum dolor sit      │
 *   │     amet consectetur. Description    │
 *   │     flows like an article body,      │
 *   │     two columns on wide screens.     │
 *   │                                      │
 *   │ ── widgets / socials / audio ──      │
 *   └──────────────────────────────────────┘
 *
 * Serif typography, drop-cap first letter of the username, 2-column
 * description on wide viewports. Unique and reads like a magazine column.
 */
export function renderMagazine(
  config: ProfileConfig,
  options: { gateOpened: Promise<void> } = { gateOpened: Promise.resolve() },
): HTMLElement {
  const p = renderProfileParts(config, options);
  p.card.classList.add("card--magazine");

  const masthead = el("div", { class: "magazine__masthead" }, [
    el("div", { class: "magazine__issue" }, [`ISSUE ${new Date().getFullYear()}·${(new Date().getMonth()+1).toString().padStart(2,"0")}`]),
    el("div", { class: "magazine__pub" }, [config.meta.title || "PROFILE"]),
  ]);

  const headline = el("div", { class: "magazine__headline" }, [
    p.avatar,
    el("div", { class: "magazine__title-block" }, [
      el("div", { class: "card__name-row magazine__name-row" }, [p.username, p.badges]),
      el("div", { class: "magazine__byline" }, [`by ${config.profile.username}`]),
    ]),
  ]);

  // No drop-cap — the typewriter description flows cleanly in serif without
  // a floating capital clashing against the URL-style content. The masthead
  // + serif typography already deliver the editorial feel.
  const article = el("div", { class: "magazine__article" }, [p.description]);

  const pullquote = el("blockquote", { class: "magazine__pullquote" }, [
    el("span", { class: "magazine__pullquote-mark" }, ["“"]),
    el("span", {}, [config.profile.location || "here."]),
    el("span", { class: "magazine__pullquote-mark" }, ["”"]),
  ]);

  const meta = el("div", { class: "card__meta magazine__meta" });
  const vc = p.meta.getInline("views");    if (vc) meta.append(vc);
  const loc = p.meta.getInline("location"); if (loc) meta.append(loc);
  const clk = p.meta.getInline("clock");    if (clk) meta.append(clk);
  const ct = p.meta.getInline("contact");
  if (ct && p.contact) {
    const corner = el("div", { class: "card__corner card__corner--top-right" }, [ct]);
    p.card.append(corner);
  }

  p.card.append(masthead, headline, article);
  if (config.profile.location) p.card.append(pullquote);
  if (p.socials) p.card.append(p.socials);
  if (p.widgets) p.card.append(p.widgets);
  if (p.links) p.card.append(p.links);
  if (p.audio) p.card.append(p.audio);
  if (meta.childElementCount > 0) p.card.append(meta);
  p.meta.applyCornerZones();

  return p.card;
}
